import { CreateNewForecastPayload, ForecastUpdatePayload } from '@futrli/predict/forecast/dashboard/types';
import { ForecastListItem } from '@futrli-sdk/predict';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { createForecastMutation, deleteForecastMutation, updateForecastMutation } from '../../../mutations/forecasts';
import { forecastsQuery } from '../../../queries/forecasts';

export interface ModifyForecastViewState extends ApiViewState {
  errorDeleting: Error | null;
  errorDuplicating: Error | null;
  errorUpdating: Error | null;
  forecast?: ForecastListItem;
  isDeleting: boolean;
  isDuplicating: boolean;
  isUpdating: boolean;
  newForecastId?: string;
}

export class ModifyForecastView extends ApiView {
  private forecastsObs: ReturnType<typeof forecastsQuery>;

  private deleteMutation: ReturnType<typeof deleteForecastMutation>;
  private duplicateMutation: ReturnType<typeof createForecastMutation>;
  private updateMutation: ReturnType<typeof updateForecastMutation>;

  public state: ModifyForecastViewState = {
    ...getDefaultApiViewState(),
    errorDeleting: null,
    errorDuplicating: null,
    errorUpdating: null,
    forecast: undefined,
    isDeleting: false,
    isDuplicating: false,
    isUpdating: false,
    newForecastId: undefined
  };

  constructor(private workspaceId: string, private organisationId: string, private forecastId: string) {
    super();

    this.forecastsObs = this.addQuery(forecastsQuery(workspaceId, organisationId));

    this.deleteMutation = this.addMutation(deleteForecastMutation(workspaceId, organisationId));
    this.duplicateMutation = this.addMutation(createForecastMutation(workspaceId, organisationId));
    this.updateMutation = this.addMutation(updateForecastMutation(workspaceId, organisationId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public deleteForecast() {
    return this.deleteMutation.mutate(this.forecastId);
  }

  public duplicateForecast(data: CreateNewForecastPayload) {
    return this.duplicateMutation.mutate(data);
  }

  public lockForecast() {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data: { is_frozen: true } });
  }

  public publishForecast() {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data: { status: 'published' } });
  }

  public resetMutations() {
    this.deleteMutation.reset();
    this.duplicateMutation.reset();
    this.updateMutation.reset();
    this.updateState();
  }

  public unpublishForecast() {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data: { status: 'draft' } });
  }

  public updateForecast(data: ForecastUpdatePayload) {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorDuplicating() {
    return this.duplicateMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.updateMutation.getCurrentResult().error;
  }

  private get forecast() {
    const forecasts = this.getResult(this.forecastsObs).data ?? [];
    return forecasts.find(f => f.id === this.forecastId);
  }

  protected getNewState(): ModifyForecastViewState {
    return {
      ...super.getNewState(),
      errorDeleting: this.errorDeleting,
      errorDuplicating: this.errorDuplicating,
      errorUpdating: this.errorUpdating,
      forecast: this.forecast,
      isDeleting: this.isDeleting,
      isDuplicating: this.isDuplicating,
      isUpdating: this.isUpdating,
      newForecastId: this.newForecastId
    };
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isDuplicating() {
    return this.duplicateMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  private get newForecastId() {
    return this.duplicateMutation.getCurrentResult().data?.id;
  }
}
